import React from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { ARTICLES, formatPublishDate } from '@/blog/articles';

const STYLES = `
.blog-idx{background:#FAF7F0;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;color:#0D1B2A;}
.blog-idx-nav{background:#fff;border-bottom:1px solid rgba(200,168,75,.2);padding:22px 48px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:50;backdrop-filter:blur(20px);background:rgba(255,255,255,.94);}
.blog-idx-back{background:none;border:none;font-size:11px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#3A4A5C;cursor:pointer;}
.blog-idx-back:hover{color:#C8A84B;}
.blog-idx-logo{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;letter-spacing:.04em;color:#C8A84B;}
.blog-idx-logo em{font-style:italic;font-weight:300;color:#0D1B2A;}
.blog-idx-hero{padding:96px 48px 64px;max-width:1280px;margin:0 auto;border-bottom:1px solid rgba(200,168,75,.18);}
.blog-idx-eyebrow{font-size:10px;font-weight:700;letter-spacing:.32em;text-transform:uppercase;color:#C8A84B;margin-bottom:24px;display:flex;align-items:center;gap:14px;}
.blog-idx-eyebrow::before{content:'';width:40px;height:1px;background:#C8A84B;}
.blog-idx-h1{font-family:'Cormorant Display',serif;font-size:clamp(56px,7vw,92px);font-weight:700;line-height:.95;letter-spacing:-.025em;margin:0 0 20px;}
.blog-idx-h1 em{font-style:italic;color:#C8A84B;}
.blog-idx-sub{font-family:'Cormorant Display',serif;font-style:italic;font-weight:300;font-size:clamp(20px,2.2vw,26px);color:#3A4A5C;line-height:1.55;max-width:760px;margin:0;}
.blog-idx-grid{max-width:1280px;margin:0 auto;padding:80px 48px 120px;display:grid;grid-template-columns:repeat(2,1fr);gap:48px;}
.blog-card{background:#fff;border:1px solid rgba(200,168,75,.18);overflow:hidden;cursor:pointer;transition:all .3s cubic-bezier(.16,1,.3,1);display:flex;flex-direction:column;}
.blog-card:hover{border-color:#C8A84B;transform:translateY(-4px);box-shadow:0 20px 48px rgba(13,27,42,.08);}
.blog-card-cat{background:#0D1B2A;color:#C8A84B;font-size:9px;font-weight:700;letter-spacing:.24em;text-transform:uppercase;padding:14px 28px;display:flex;justify-content:space-between;align-items:center;}
.blog-card-meta-top{color:rgba(250,247,240,.6);font-size:9px;font-weight:500;letter-spacing:.14em;}
.blog-card-body{padding:32px 32px 24px;flex:1;display:flex;flex-direction:column;gap:14px;}
.blog-card-title{font-family:'Cormorant Display',serif;font-size:clamp(26px,2.6vw,34px);font-weight:700;line-height:1.1;letter-spacing:-.015em;color:#0D1B2A;margin:0;}
.blog-card-title em{font-style:italic;color:#C8A84B;}
.blog-card-excerpt{font-size:14px;color:#3A4A5C;line-height:1.7;font-weight:400;flex:1;}
.blog-card-author{display:flex;align-items:center;gap:12px;padding-top:16px;border-top:1px solid rgba(200,168,75,.15);margin-top:auto;}
.blog-card-avatar{width:40px;height:40px;border-radius:50%;background:#C8A84B;color:#0D1B2A;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Display',serif;font-style:italic;font-weight:700;font-size:16px;background-size:cover;background-position:center 15%;}
.blog-card-author-meta{flex:1;}
.blog-card-author-name{font-size:12px;font-weight:700;color:#0D1B2A;letter-spacing:.01em;}
.blog-card-author-date{font-size:11px;color:#7A8190;letter-spacing:.02em;}
.blog-card-cta{font-size:10px;font-weight:800;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;}
@media(max-width:900px){.blog-idx-grid{grid-template-columns:1fr;padding:56px 24px 96px;gap:32px;}.blog-idx-hero{padding:72px 24px 56px;}.blog-idx-nav{padding:18px 24px;}}
`;

const initials = (name) => name.split(' ').filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase();

export default function BlogIndex() {
  const navigate = useNavigate();

  useSEO({
    title: "The SKI AXIS Blog · Dubai Governance, UAE Investment & Startup Accelerator Insights",
    description: "Long-form editorial intelligence on UAE investment, governance innovation, Dubai free zones, the Golden Visa, and the SKI AXIS startup accelerator. Written by Dr. Neha Dewan and the SKI APAC research team.",
    keywords: "SKI Axis blog, UAE investment blog, Dubai startup accelerator blog, governance innovation Dubai, UAE golden visa guide, Dubai free zone 2026, UK investor UAE, governance accelerator Dubai",
    canonical: "/blog",
    ogImage: "/og-default.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "The SKI AXIS Blog",
      "url": "https://skiaxis.com/blog",
      "description": "Editorial intelligence on UAE investment, governance innovation, and startup acceleration.",
      "publisher": { "@type": "Organization", "name": "SKI AXIS", "url": "https://skiaxis.com" },
      "blogPost": ARTICLES.map(a => ({
        "@type": "BlogPosting",
        "headline": a.title,
        "url": `https://skiaxis.com/blog/${a.slug}`,
        "datePublished": a.publishedAt,
        "author": { "@type": "Person", "name": a.author.name },
      })),
    },
  });

  return (
    <div className="blog-idx" data-testid="blog-index-page">
      <style>{STYLES}</style>
      <nav className="blog-idx-nav">
        <button className="blog-idx-back" onClick={() => navigate('/')} data-testid="blog-idx-back">← SKI Axis</button>
        <div className="blog-idx-logo">SKI <em>AXIS</em></div>
      </nav>
      <div className="blog-idx-hero">
        <div className="blog-idx-eyebrow">Editorial · Intelligence · Insights</div>
        <h1 className="blog-idx-h1">The SKI AXIS <em>Blog</em></h1>
        <p className="blog-idx-sub">Long-form intelligence on UAE investment, governance innovation, and the next decade of cross-border capital — written by Dr. Neha Dewan and the SKI APAC research team.</p>
        <div style={{ marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/rss.xml`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-rss-feed"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: '#0D1B2A', background: 'transparent', border: '1px solid rgba(200,168,75,.45)', padding: '11px 20px', borderRadius: 50, textDecoration: 'none', transition: 'all .2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#C8A84B'; e.currentTarget.style.borderColor = '#C8A84B'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(200,168,75,.45)'; }}
          >
            <span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid currentColor', borderRadius: '50%', borderRightColor: 'transparent', borderBottomColor: 'transparent' }} />
            RSS Feed
          </a>
          <span style={{ fontSize: 12, color: '#3A4A5C', lineHeight: 1.45, maxWidth: 480 }}>
            Wire this RSS feed to <strong>Zapier, Buffer, Make, or IFTTT</strong> to auto-post every new SKI AXIS article to your LinkedIn, X, or Facebook page.
          </span>
        </div>
      </div>
      <div className="blog-idx-grid">
        {ARTICLES.map((a) => (
          <article
            key={a.slug}
            className="blog-card"
            onClick={() => navigate(`/blog/${a.slug}`)}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/blog/${a.slug}`); } }}
            data-testid={`blog-card-${a.slug}`}
          >
            <div className="blog-card-cat">
              <span>{a.category}</span>
              <span className="blog-card-meta-top">{a.readTimeMin} min read</span>
            </div>
            <div className="blog-card-body">
              <h2 className="blog-card-title">{a.title}</h2>
              <p className="blog-card-excerpt">{a.excerpt}</p>
              <div className="blog-card-author">
                <div className="blog-card-avatar" style={a.author.photo ? { backgroundImage: `url(${a.author.photo})`, color: 'transparent' } : {}}>{initials(a.author.name)}</div>
                <div className="blog-card-author-meta">
                  <div className="blog-card-author-name">{a.author.name}</div>
                  <div className="blog-card-author-date">{a.author.role} · {formatPublishDate(a.publishedAt)}</div>
                </div>
                <div className="blog-card-cta">Read →</div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
